<template>
  <div class="backup-page">
    <div class="page-header">
      <div>
        <h2>小程序菜品备份文件管理</h2>
        <p>管理用户从小程序导出的菜品 Excel 备份文件。</p>
      </div>
      <button class="btn primary" @click="loadBackups">刷新</button>
    </div>

    <div class="stats-row">
      <div class="stat-item">
        <span class="stat-label">备份文件</span>
        <strong>{{ stats.backup_count || 0 }}</strong>
      </div>
      <div class="stat-item">
        <span class="stat-label">菜品总数</span>
        <strong>{{ stats.dish_total || 0 }}</strong>
      </div>
      <div class="stat-item">
        <span class="stat-label">分类总数</span>
        <strong>{{ stats.category_total || 0 }}</strong>
      </div>
    </div>

    <div class="toolbar">
      <input
        v-model.trim="search.keyword"
        class="input"
        placeholder="搜索备份编号、openid、文件名"
        @keyup.enter="handleSearch"
      />
      <input
        v-model.trim="search.openid"
        class="input openid-input"
        placeholder="精确 openid"
        @keyup.enter="handleSearch"
      />
      <input v-model="search.startDate" type="date" class="input date-input" />
      <span class="date-separator">至</span>
      <input v-model="search.endDate" type="date" class="input date-input" />
      <button class="btn primary" @click="handleSearch">查询</button>
      <button class="btn reset-btn" @click="resetSearch">重置</button>
    </div>

    <div class="table-wrap">
      <table class="backup-table">
        <thead>
          <tr>
            <th>备份编号</th>
            <th>用户 OpenID</th>
            <th>文件名</th>
            <th>菜品</th>
            <th>分类</th>
            <th>创建时间</th>
            <th>恢复时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="8" class="empty">加载中...</td>
          </tr>
          <tr v-else-if="backupList.length === 0">
            <td colspan="8" class="empty">暂无备份文件</td>
          </tr>
          <tr v-for="item in backupList" :key="item.backup_no">
            <td class="mono">{{ item.backup_no }}</td>
            <td class="openid-cell" :title="item.openid">{{ item.openid }}</td>
            <td>{{ item.file_name || '-' }}</td>
            <td>{{ item.dish_count || 0 }}</td>
            <td>{{ item.category_count || 0 }}</td>
            <td>{{ item.created_at || '-' }}</td>
            <td>{{ item.restored_at || '-' }}</td>
            <td>
              <button class="link-btn" @click="downloadBackup(item)">下载</button>
              <button class="danger-btn" @click="deleteBackup(item)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button class="btn plain-btn" :disabled="page.page === 1 || loading" @click="changePage(page.page - 1)">
        上一页
      </button>
      <span>第 {{ page.page }} 页 / 共 {{ totalPages }} 页，共 {{ total }} 条</span>
      <button class="btn plain-btn" :disabled="page.page >= totalPages || loading" @click="changePage(page.page + 1)">
        下一页
      </button>
      <select v-model.number="page.pageSize" class="page-size" @change="handlePageSizeChange">
        <option :value="10">10 条/页</option>
        <option :value="20">20 条/页</option>
        <option :value="50">50 条/页</option>
        <option :value="100">100 条/页</option>
      </select>
    </div>
  </div>
</template>

<script>
import { getDishBackupFiles, deleteDishBackupFile } from '@/api/houtai/dishBackup'

export default {
  name: 'DishBackupFiles',
  data() {
    return {
      loading: false,
      backupList: [],
      total: 0,
      stats: {
        backup_count: 0,
        dish_total: 0,
        category_total: 0
      },
      page: {
        page: 1,
        pageSize: 20
      },
      search: {
        keyword: '',
        openid: '',
        startDate: '',
        endDate: ''
      }
    }
  },
  computed: {
    totalPages() {
      return Math.max(Math.ceil(this.total / this.page.pageSize), 1)
    }
  },
  mounted() {
    this.loadBackups()
  },
  methods: {
    async loadBackups() {
      this.loading = true
      try {
        const res = await getDishBackupFiles({
          page: this.page.page,
          pageSize: this.page.pageSize,
          keyword: this.search.keyword,
          openid: this.search.openid,
          startDate: this.search.startDate,
          endDate: this.search.endDate
        })
        if (res.status === 200) {
          this.backupList = res.data || []
          this.total = res.total || 0
          this.stats = res.stats || this.stats
        } else {
          alert(res.error || '获取备份文件失败')
        }
      } catch (error) {
        console.error('获取备份文件失败', error)
        alert('获取备份文件失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.page = 1
      this.loadBackups()
    },
    resetSearch() {
      this.search = {
        keyword: '',
        openid: '',
        startDate: '',
        endDate: ''
      }
      this.handleSearch()
    },
    changePage(nextPage) {
      this.page.page = nextPage
      this.loadBackups()
    },
    handlePageSizeChange() {
      this.page.page = 1
      this.loadBackups()
    },
    downloadBackup(item) {
      if (!item.file_url) {
        alert('该备份文件没有下载地址')
        return
      }
      window.open(item.file_url, '_blank')
    },
    async deleteBackup(item) {
      if (!confirm(`确定删除备份文件 ${item.backup_no} 吗？`)) return
      try {
        const res = await deleteDishBackupFile(item.backup_no)
        if (res.status === 200) {
          alert(res.message || '删除成功')
          if (this.backupList.length === 1 && this.page.page > 1) {
            this.page.page -= 1
          }
          this.loadBackups()
        } else {
          alert(res.error || '删除失败')
        }
      } catch (error) {
        console.error('删除备份文件失败', error)
        alert('删除备份文件失败')
      }
    }
  }
}
</script>

<style scoped>
.backup-page {
  padding: 18px;
  color: #25283d;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Microsoft YaHei", sans-serif;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.page-header h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 650;
}

.page-header p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.stat-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px 16px;
  background: #ffffff;
}

.stat-label {
  display: block;
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 8px;
}

.stat-item strong {
  font-size: 24px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 16px;
}

.input,
.page-size {
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 10px;
  outline: none;
}

.input {
  width: 250px;
}

.openid-input {
  width: 280px;
}

.date-input {
  width: 150px;
}

.date-separator {
  color: #6b7280;
}

.btn,
.link-btn,
.danger-btn {
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0 14px;
  background: #fff;
  cursor: pointer;
}

.btn.primary {
  border-color: #854685;
  background: #854685;
  color: #fff;
}

.reset-btn,
.plain-btn {
  color: #111827;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.backup-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
}

.backup-table th,
.backup-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #edf0f3;
  text-align: left;
  font-size: 14px;
}

.backup-table th {
  background: #f9fafb;
  color: #374151;
  font-weight: 650;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

.openid-cell {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty {
  text-align: center;
  color: #6b7280;
  padding: 32px 10px;
}

.link-btn {
  margin-right: 8px;
  color: #2563eb;
}

.danger-btn {
  color: #dc2626;
  border-color: #fecaca;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
  color: #4b5563;
}

@media (max-width: 768px) {
  .page-header,
  .pagination {
    align-items: flex-start;
    flex-direction: column;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .input,
  .openid-input,
  .date-input {
    width: 100%;
  }
}
</style>
